import React from "react";
import { Badge, Form, FormGroup, Row } from "react-bootstrap";
import {
  DocuClipperBillingCycle,
  DocuClipperPlan,
  DocuClipperPricing,
  StripePlan,
  StripeSubscription,
} from "../../../../docuclipper/DocuclipperTypes";
import { getCurrencySymbol } from "./PlanSelector2";
import { getParentPlanName } from "./tierUtils";

type Props = {
  onSelected: (billingCycle: DocuClipperBillingCycle) => any;
  pricing: DocuClipperPricing;
  discountedPricing: DocuClipperPricing | null;
  selectedPlan: DocuClipperPlan;
  selectedBillingCycle: DocuClipperBillingCycle;
  subscription: StripeSubscription;
  billingCycleOptions?: DocuClipperBillingCycle[] | null;
  plans?: StripePlan[];
};
export const BillingCycleSelector: React.FC<Props> = ({
  onSelected,
  pricing,
  discountedPricing,
  selectedPlan,
  selectedBillingCycle,
  subscription,
  billingCycleOptions,
  plans,
}) => {
  // console.log({ pricing, billingCycleOptions });
  const getDiscounted = (originalPrice) => {
    if (!subscription || !subscription.coupon) {
      return originalPrice;
    }
    return Math.round((originalPrice * (100 - subscription.coupon.percent_off)) / 100);
  };

  // Find actual tier plans from plans array
  const monthlyPlan = plans?.find(
    (p) =>
      p.docuclipperPlan.name === selectedPlan &&
      p.docuclipperPlan.billingCycle === "Monthly"
  );
  const annualPlan = plans?.find(
    (p) =>
      p.docuclipperPlan.name === selectedPlan &&
      p.docuclipperPlan.billingCycle === "Annually"
  );
  const annualBilledMonthlyPlan = plans?.find(
    (p) =>
      p.docuclipperPlan.name === selectedPlan &&
      p.docuclipperPlan.billingCycle === "AnnuallyBilledMonthly"
  );

  // Determine which plan to look up in pricing object as fallback
  // For tier plans like "Starter 60", we need to use the parent plan name
  let planKeyForPricing = selectedPlan;

  // If plans array is provided, try to find the parent plan
  if (plans && plans.length > 0) {
    const matchingPlan = plans.find(
      (p) => p.docuclipperPlan.name === selectedPlan
    );
    if (matchingPlan) {
      // Use parent plan name if available, otherwise extract from name
      const parentName = getParentPlanName(matchingPlan);
      if (parentName && pricing[parentName]) {
        planKeyForPricing = parentName as DocuClipperPlan;
      }
    }
  }

  // If the plan key doesn't exist in pricing, try to extract parent plan name
  if (!pricing[planKeyForPricing]) {
    // Try to extract parent from name (e.g., "Starter 60" -> "Starter")
    const match = selectedPlan.match(
      /^(Starter|Business|Enterprise|Professional)/
    );
    if (match && pricing[match[1]]) {
      planKeyForPricing = match[1] as DocuClipperPlan;
    }
  }

  // Use tier-specific pricing if available, otherwise fall back to pricing object
  const monthlyAmount = monthlyPlan
    ? Math.round(monthlyPlan.amount / 100)
    : Math.round(pricing[planKeyForPricing]["Monthly"].amount);
  const monthlyAmountDiscounted = monthlyPlan
    ? getDiscounted(monthlyAmount)
    : discountedPricing
    ? Math.round(discountedPricing[planKeyForPricing]["Monthly"].amount)
    : monthlyAmount;

  const annualAmount = annualPlan
    ? Math.floor(annualPlan.amount / 100)
    : Math.floor(pricing[planKeyForPricing]["Annually"].amount);
  const annualAmountDiscounted = annualPlan
    ? getDiscounted(annualAmount)
    : discountedPricing
    ? Math.floor(discountedPricing[planKeyForPricing]["Annually"].amount)
    : annualAmount;

  const monthlyAmountInAnnualPlan = Math.floor(annualAmount / 12);
  const monthlyAmountInAnnualPlanDiscounted = Math.floor(
    annualAmountDiscounted / 12
  );

  const currencySymbol = annualPlan
    ? getCurrencySymbol(annualPlan.currency)
    : getCurrencySymbol(pricing[planKeyForPricing]["Annually"].currency);
  const annualLabel = subscription?.coupon && monthlyAmountInAnnualPlanDiscounted !== monthlyAmountInAnnualPlan ? (
    <>
      <del className="text-muted mr-2" style={{ fontSize: "0.85em", opacity: 0.6 }}>{`${currencySymbol}${monthlyAmountInAnnualPlan}`}</del>
      <span style={{ fontWeight: "bold" }}>{`${currencySymbol}${monthlyAmountInAnnualPlanDiscounted} / month, annual plan billed upfront`}</span>
    </>
  ) : (
    `${currencySymbol}${monthlyAmountInAnnualPlanDiscounted} / month, annual plan billed upfront`
  );

  const annualSavings = annualPlan
    ? Math.round(monthlyAmount * 12 - annualAmount)
    : Math.round(pricing[planKeyForPricing]["Annually"].annualSavings);
  const annualSavingsDiscounted = getDiscounted(annualSavings);

  const annualBilledMonthlyAmount = annualBilledMonthlyPlan
    ? Math.floor(annualBilledMonthlyPlan.amount / 100)
    : Math.floor(pricing[planKeyForPricing]["AnnuallyBilledMonthly"].amount);
  const annualBilledMonthlyAmountDiscounted = annualBilledMonthlyPlan
    ? getDiscounted(annualBilledMonthlyAmount)
    : discountedPricing
    ? Math.floor(discountedPricing[planKeyForPricing]["AnnuallyBilledMonthly"].amount)
    : annualBilledMonthlyAmount;
  const annualBilledMonthlyLabel = subscription?.coupon && annualBilledMonthlyAmountDiscounted !== annualBilledMonthlyAmount ? (
    <>
      <del className="text-muted mr-2" style={{ fontSize: "0.85em", opacity: 0.6 }}>{`${currencySymbol}${annualBilledMonthlyAmount}`}</del>
      <span style={{ fontWeight: "bold" }}>{`${currencySymbol}${annualBilledMonthlyAmountDiscounted} / month, annual plan billed monthly, 12-month commitment`}</span>
    </>
  ) : (
    <>{`${currencySymbol}${annualBilledMonthlyAmountDiscounted} / month, annual plan billed monthly, 12-month commitment`}</>
  );
  const annualBilledMonthlySavings = annualBilledMonthlyPlan
    ? Math.round(monthlyAmount * 12 - annualBilledMonthlyAmount * 12)
    : Math.round(
        pricing[planKeyForPricing]["AnnuallyBilledMonthly"].annualSavings
      );
  const annualBilledMonthlySavingsDiscounted = getDiscounted(
    annualBilledMonthlySavings
  );

  return (
    <FormGroup className="m-2">
      {(!billingCycleOptions ||
        billingCycleOptions.includes("Monthly") ||
        billingCycleOptions.includes("AnnuallyBilledMonthly")) &&
        selectedPlan !== "Starter100" && (
          <FormGroup
            className={`option-container ${
              selectedBillingCycle === "Monthly" ? "selected-option" : ""
            }`}
            onClick={() => onSelected("Monthly")}
          >
            {
              <div className="p-1">
                <Form.Check
                  type="radio"
                  value="Monthly"
                  checked={selectedBillingCycle === "Monthly"}
                  onChange={() => onSelected("Monthly")}
                  label={
                    <>
                      <div className="price-details">
                        {subscription?.coupon && monthlyAmountDiscounted !== monthlyAmount ? (
                          <>
                            <del className="text-muted mr-2" style={{ fontSize: "0.85em", opacity: 0.6 }}>{`${currencySymbol}${monthlyAmount}`}</del>
                            <span style={{ fontWeight: "bold" }}>{`${currencySymbol}${monthlyAmountDiscounted} / month`}</span>
                          </>
                        ) : (
                          `${currencySymbol}${monthlyAmountDiscounted} / month`
                        )}
                      </div>
                      <div className="text-primary small mt-1">
                        Save ~30% with annual billing. All pages available
                        upfront for the whole year.
                      </div>
                    </>
                  }
                />
              </div>
            }
          </FormGroup>
        )}
      {(!billingCycleOptions || billingCycleOptions.includes("Annually")) && (
        <FormGroup
          className={`option-container ${
            selectedBillingCycle === "Annually" ? "selected-option" : ""
          }`}
          onClick={() => onSelected("Annually")}
        >
          <Row className="p-1 align-items-center mx-0">
            <div>
              <Form.Check
                type="radio"
                value="Annually"
                checked={selectedBillingCycle === "Annually"}
                onChange={() => onSelected("Annually")}
                label={
                  <>
                    <div className="price-details">{annualLabel}</div>
                    <div className="text-success small mt-1">
                      <Badge variant="success" className="mr-2">
                        Best Value
                      </Badge>
                      Get all pages upfront to use anytime throughout the year!
                    </div>
                  </>
                }
              />
            </div>
            <div className="savings-text ml-2 mt-1">
              {`Save ${currencySymbol}${annualSavingsDiscounted} per year!`}
            </div>
          </Row>
        </FormGroup>
      )}
    </FormGroup>
  );
};
export default BillingCycleSelector;
